import posthog from "posthog-js";
import type { CompanyInformation } from "@/core/api/data-contracts";

// Same setup as HubbVue. The project key is public (write-only, ships to the browser), so it's
// hardcoded: every environment gets it without build-time env vars. Filter environments on $host.
const POSTHOG_KEY = "phc_nA8vSbWUssKXPjBzMWvTRzAnTdVFKM3hrzMSRnZdemox";
const POSTHOG_HOST = "https://eu.i.posthog.com";

export function isPostHogEnabled() {
  return posthog.__loaded;
}

export function initPostHog() {
  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    defaults: "2026-01-30",
    capture_exceptions: true,
    // Event quota: only explicit events and pageviews. Pageleave only feeds bounce rate and time on page.
    autocapture: false,
    capture_pageleave: false,
  });
}

const SENT_PERSON_KEY = "posthog_sent_person";

/**
 * identify() with properties sends a $set on every app load, even when nothing changed (posthog-js
 * only skips duplicates within one page load). So the properties go along only when they differ
 * from the last ones this browser sent for this user.
 */
export function identifyPostHogUser(userId: string, properties: Record<string, unknown>) {
  if (!isPostHogEnabled()) {
    return;
  }
  const sent = JSON.stringify([userId, properties]);
  let unchanged = false;
  try {
    unchanged = localStorage.getItem(SENT_PERSON_KEY) === sent;
  } catch {
    // No storage: send them.
  }
  posthog.identify(userId, unchanged ? undefined : properties);
  if (!unchanged) {
    try {
      localStorage.setItem(SENT_PERSON_KEY, sent);
    } catch {
      // Sent again next load; no harm.
    }
  }
}

/** Company is per-session context for multi-company users, so it goes on every event rather than only the person. */
export function setPostHogCompany(company: CompanyInformation | null) {
  if (isPostHogEnabled()) {
    posthog.register({ company_id: company?.id ?? null, company_name: company?.name ?? null });
  }
}

export function isPostHogUserIdentified() {
  return isPostHogEnabled() && posthog.get_property("$user_state") === "identified";
}

/** Forget the identified user (also clears registered properties). */
export function resetPostHog() {
  if (isPostHogEnabled()) {
    posthog.reset();
  }
}

export function capturePostHogException(error: unknown) {
  if (isPostHogEnabled()) {
    posthog.captureException(error);
  }
}
